import { useEffect, useState } from "react";
import { motion, useSpring, MotionValue } from "framer-motion";
import { durations, easings, delays } from "./transitions";

/**
 * Reusable animated components for consistent animations across the app.
 */

// ============================================
// ANIMATED PROGRESS BAR
// ============================================

interface AnimatedProgressProps {
  /** Progress value from 0 to 100 */
  value: number;
  /** Optional custom class for the progress bar fill */
  className?: string;
  /** Optional custom class for the background track */
  trackClassName?: string;
  /** Delay before animation starts (in seconds) */
  delay?: number;
}

export function AnimatedProgress({
  value,
  className = "bg-primary h-full rounded-full shadow-[0_0_10px_rgba(37,244,120,0.5)]",
  trackClassName = "w-full bg-white/10 rounded-full h-1",
  delay = delays.medium,
}: AnimatedProgressProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className={trackClassName}>
      <motion.div
        className={className}
        initial={{ width: "0%" }}
        animate={{ width: mounted ? `${Math.min(100, Math.max(0, value))}%` : "0%" }}
        transition={{
          duration: durations.progress,
          ease: easings.easeOut,
          delay,
        }}
      />
    </div>
  );
}

// ============================================
// ANIMATED CIRCULAR PROGRESS (SVG Ring)
// ============================================

interface AnimatedCircularProgressProps {
  /** Progress value from 0 to 100 */
  value: number;
  /** Size of the SVG viewBox (default 100) */
  size?: number;
  /** Radius of the circle */
  radius?: number;
  /** Stroke width */
  strokeWidth?: number;
  /** Track color class */
  trackClassName?: string;
  /** Progress color class */
  progressClassName?: string;
  /** Delay before animation starts (in seconds) */
  delay?: number;
  /** Children to render in center */
  children?: React.ReactNode;
}

export function AnimatedCircularProgress({
  value,
  size = 100,
  radius = 40,
  strokeWidth = 8,
  trackClassName = "text-white/10",
  progressClassName = "text-primary drop-shadow-[0_0_4px_rgba(37,244,120,0.8)]",
  delay = delays.medium,
  children,
}: AnimatedCircularProgressProps) {
  const [mounted, setMounted] = useState(false);
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    setMounted(true);
  }, []);

  const targetOffset = circumference - (Math.min(100, Math.max(0, value)) / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        className="size-full -rotate-90 transform"
        viewBox={`0 0 ${size} ${size}`}
      >
        {/* Background track */}
        <circle
          className={trackClassName}
          cx={size / 2}
          cy={size / 2}
          fill="transparent"
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
        />
        {/* Animated progress */}
        <motion.circle
          className={progressClassName}
          cx={size / 2}
          cy={size / 2}
          fill="transparent"
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: mounted ? targetOffset : circumference }}
          transition={{
            duration: durations.progress,
            ease: easings.easeOut,
            delay,
          }}
        />
      </svg>
      {children && (
        <div className="absolute inset-0 flex items-center justify-center">
          {children}
        </div>
      )}
    </div>
  );
}

// ============================================
// ANIMATED NUMBER (Count up)
// ============================================

interface AnimatedNumberProps {
  /** Target number value */
  value: number;
  /** Number of decimal places */
  decimals?: number;
  /** Prefix (e.g., "$") */
  prefix?: string;
  /** Suffix (e.g., "km") */
  suffix?: string;
  /** Custom class for the number */
  className?: string;
  /** Duration of the animation in seconds */
  duration?: number;
  /** Format with commas (e.g., 1,234) */
  formatWithCommas?: boolean;
}

function useAnimatedNumber(
  value: number,
  duration: number = durations.progress
): MotionValue<number> {
  const spring = useSpring(0, {
    stiffness: 50,
    damping: 20,
    duration: duration * 1000,
  });

  useEffect(() => {
    spring.set(value);
  }, [spring, value]);

  return spring;
}

export function AnimatedNumber({
  value,
  decimals = 0,
  prefix = "",
  suffix = "",
  className = "",
  duration = durations.progress,
  formatWithCommas = false,
}: AnimatedNumberProps) {
  const animatedValue = useAnimatedNumber(value, duration);
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const unsubscribe = animatedValue.on("change", (latest: number) => {
      setDisplayValue(latest);
    });
    return unsubscribe;
  }, [animatedValue]);

  const formattedValue = formatWithCommas
    ? displayValue.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : displayValue.toFixed(decimals);

  return (
    <span className={className}>
      {prefix}
      {formattedValue}
      {suffix}
    </span>
  );
}

// ============================================
// ANIMATED SVG PATH (Line drawing effect)
// ============================================

interface AnimatedPathProps {
  /** SVG path d attribute */
  d: string;
  /** Stroke color */
  stroke?: string;
  /** Stroke width */
  strokeWidth?: number;
  /** Additional className */
  className?: string;
  /** Animation delay */
  delay?: number;
  /** Animation duration */
  duration?: number;
}

export function AnimatedPath({
  d,
  stroke = "#25f478",
  strokeWidth = 3,
  className = "",
  delay = delays.medium,
  duration = durations.progress,
}: AnimatedPathProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <motion.path
      d={d}
      fill="none"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      className={className}
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{
        pathLength: mounted ? 1 : 0,
        opacity: mounted ? 1 : 0
      }}
      transition={{
        pathLength: {
          duration,
          ease: easings.easeOut,
          delay,
        },
        opacity: {
          duration: 0.2,
          delay,
        },
      }}
    />
  );
}

// ============================================
// ANIMATED CONTAINER (Fade in on mount)
// ============================================

interface AnimatedContainerProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}

export function AnimatedContainer({
  children,
  className = "",
  delay = 0,
}: AnimatedContainerProps) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: durations.normal,
        ease: easings.easeOut,
        delay,
      }}
    >
      {children}
    </motion.div>
  );
}
